import { FlagProgressType, getFlaggingReasons, getReportHeaders, useTranslation } from "@next-platform/client-common-core";
import { Button, ButtonSize, Modal, RadioGroup, RadioGroupSize } from "@next-platform/client-common-spicy-ui";
import { useState } from "react";

import Trans from "@/components/Trans/Trans";

import { FlagModalProps } from "./types";

export default function FlagModal(props: FlagModalProps) {
  const { t } = useTranslation();
  const flaggingReasons = getFlaggingReasons(t);
  const reportHeaders = getReportHeaders(t);
  const translatedReasonsList = flaggingReasons?.map((reason: { label: string; value: string }) => ({
    ...reason,
    label: reason.label,
  }));
  const { isOpen, handleFlagMessage, onClose, isLoading, progressStep, setProgressStep } = props;

  const [selectedReason, setSelectedReason] = useState("");
  const [comment, setComment] = useState("");
  const isDisabled = progressStep === FlagProgressType.SELECT_OPTION;

  const getHeaderDescription = (): { description: string; subDescription: string } | undefined => {
    switch (progressStep) {
      case FlagProgressType.SELECT_OPTION:
      case FlagProgressType.OPTION_SELECTED:
        return reportHeaders?.[FlagProgressType.SELECT_OPTION];
      case FlagProgressType.ADD_COMMENT:
        return reportHeaders?.[FlagProgressType.ADD_COMMENT];
      case FlagProgressType.DONE:
        return reportHeaders?.[FlagProgressType.DONE];
      case FlagProgressType.ERROR:
        return reportHeaders?.[FlagProgressType.ERROR];
      default:
        return undefined;
    }
  };
  const { description, subDescription } = getHeaderDescription() ?? {};

  const renderButtonLabel = () => {
    if (progressStep === FlagProgressType.ADD_COMMENT && !comment?.trim()) {
      return <Trans i18nKey="common:skip" />;
    } else if (progressStep === FlagProgressType.DONE) {
      return <Trans i18nKey="common:done" />;
    } else if (progressStep === FlagProgressType.ERROR) {
      return <Trans i18nKey="common:close" />;
    } else {
      return <Trans i18nKey="common:next" />;
    }
  };

  const handleValueChange = (value: string) => {
    setProgressStep(FlagProgressType.OPTION_SELECTED);
    setSelectedReason(value);
  };

  const resetModal = () => {
    setComment("");
    setProgressStep(FlagProgressType.SELECT_OPTION);
    setSelectedReason("");
  };
  const handleChatReportModalClose = () => {
    resetModal();
    onClose();
  };

  const handleComment = (text: string) => {
    setComment(text);
  };

  const handleNext = () => {
    switch (progressStep) {
      case FlagProgressType.OPTION_SELECTED:
        return setProgressStep(FlagProgressType.ADD_COMMENT);
      case FlagProgressType.ADD_COMMENT:
        return handleFlagMessage({ comment, reason: selectedReason });
      case FlagProgressType.DONE:
      case FlagProgressType.ERROR:
        return handleChatReportModalClose();
    }
  };

  const renderModalContent = () => {
    switch (progressStep) {
      case FlagProgressType.SELECT_OPTION:
      case FlagProgressType.OPTION_SELECTED:
        return (
          <div className="pt-[1px]">
            <RadioGroup
              name="FlagMessageReason"
              items={translatedReasonsList}
              value={selectedReason}
              onValueChange={handleValueChange}
              size={RadioGroupSize.LARGE}
            />
          </div>
        );
      case FlagProgressType.ADD_COMMENT:
        return (
          <>
            <textarea
              placeholder={t("common:reason")}
              onChange={(e) => {
                handleComment(e.target.value);
              }}
              value={comment}
              className="bg-gray-3 rounded-md p-md border-1 border-solid border-gray-10 resize-none text-medium w-full"
              rows={4}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Modal
        className="min-h-[300px]"
        title={t("common:report")}
        description={description}
        secondaryDescription={subDescription}
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleChatReportModalClose();
          }
        }}
      >
        {renderModalContent()}
        <div className="flex flex-1 flex-col justify-end">
          <Button size={ButtonSize.LARGE} onClick={handleNext} disabled={isDisabled || isLoading} loading={isLoading} aria-label={t("common:next")}>
            {renderButtonLabel()}
          </Button>
        </div>
      </Modal>
    </>
  );
}
